/**
 * 健康习惯 V1/V2 版本策略（P0-0.1，见 docs/health-habit-v2-feasibility.md §6、D12）
 *
 * 纯函数模块：不碰 React、不碰网络、不读全局，便于单测。
 *
 * 职责边界（D12 不变式）：这里只做「用哪个版本渲染」的选择，可用性只认服务端下发的
 * available / defaultVersion（P1-1.5 起）。palmer 是内部管理台，按 D13 一刀切、不做灰度，
 * 所以这里既不实现、也不预留 grayPercent / whitelist 一类字段 —— 前端自行按 userId 推导
 * 可用性就是把判定逻辑搬到前端，重演 P7。
 */

/** 操作人维度的偏好存储 key（§6：按操作人记忆，不按被查看用户） */
export const STORAGE_KEY = 'hh.viewVersion';

/** 当前 v2 契约版本；不匹配的本地偏好一律失效（§6 前端持久化行，为 v3 预留） */
export const SUPPORTED_SCHEMA_VERSION = 'hh-v2.0';

export const VIEW_V1 = 'V1';
export const VIEW_V2 = 'V2';

function safeGet(storage, key) {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    // 隐私模式 / 配额异常：存储不可用不等于出错，按"无偏好"处理
    return null;
  }
}

function safeSet(storage, key, value) {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
}

function safeRemove(storage, key) {
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * 解析本地偏好。任何不合法输入都降级为 null（= 无偏好，听服务端），绝不抛错。
 * 失效条件：JSON 损坏、结构缺失、version 非 V1/V2、schemaVersion 与当前不符。
 */
export function parsePreference(
  raw,
  { schemaVersion = SUPPORTED_SCHEMA_VERSION } = {},
) {
  if (typeof raw !== 'string' || !raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return null;
  const { version, schemaVersion: storedSchema } = parsed;
  if (version !== VIEW_V1 && version !== VIEW_V2) return null;
  if (storedSchema !== schemaVersion) return null;
  return {
    version,
    schemaVersion: storedSchema,
    chosenAt: parsed.chosenAt || null,
  };
}

/** 写入偏好（只写已知字段，避免把任意对象塞进 localStorage） */
export function writePreference(
  storage,
  version,
  { now = Date.now(), schemaVersion = SUPPORTED_SCHEMA_VERSION } = {},
) {
  if (version !== VIEW_V1 && version !== VIEW_V2) return false;
  return safeSet(
    storage,
    STORAGE_KEY,
    JSON.stringify({ version, schemaVersion, chosenAt: now }),
  );
}

export function readPreference(storage, options) {
  return parsePreference(safeGet(storage, STORAGE_KEY), options);
}

/**
 * 清掉操作人的版本偏好，回到"跟随服务端默认"。
 * P0 生产路径不调用它（服务端关停时故意保留偏好，开关再打开能无损回到他原来的选择）；
 * 它是契约漂移时的逃生阀，也是测试里构造干净存储的手段。
 */
export function clearPreference(storage) {
  return safeRemove(storage, STORAGE_KEY);
}

/**
 * 把 /healthHabit/version 的响应收敛成一个可判定的状态对象。
 * 任何失败（网络、非 00000、结构缺失）都是 ok:false —— 关键区分：
 * 服务故障与"这版本没开"都不能让页面变空（P3/R2 的同源问题），但也不能谎报可用。
 */
export function normalizeVersionStatus(result) {
  if (!result || typeof result !== 'object') {
    return { ok: false, reason: 'EMPTY_RESPONSE', data: null };
  }
  if (result.ok === false) {
    return { ok: false, reason: result.reason || 'UPSTREAM_ERROR', data: null };
  }
  // 服务层负责拆 { code, data } 信封，正常出口是 { ok:true, data }
  if (result.ok === true) {
    const unwrapped = result.data;
    if (!unwrapped || typeof unwrapped !== 'object') {
      return { ok: false, reason: 'MALFORMED_RESPONSE', data: null };
    }
    return { ok: true, reason: null, data: unwrapped };
  }
  // 兜底：直接喂原始信封也能解（防止调用方绕开服务层）
  const code =
    result.code !== undefined ? result.code : result.data && result.data.code;
  if (code !== undefined && String(code) !== '00000') {
    return {
      ok: false,
      reason: 'UPSTREAM_ERROR',
      code: String(code),
      data: null,
    };
  }
  const data = result.data || result;
  if (!data || typeof data !== 'object') {
    return { ok: false, reason: 'MALFORMED_RESPONSE', data: null };
  }
  return { ok: true, reason: null, data };
}

/**
 * 该用户当前能否看到切到 V2 的入口。
 * 三重门：V2 组件已打进包（P1-1.7 之前为 false）+ 服务端 available + 未处于降级态。
 */
export function canOfferV2({ v2Bundled, status, degraded }) {
  if (degraded) return false;
  if (!v2Bundled) return false;
  if (!status || status.ok !== true) return false;
  return status.data.available === true;
}

/**
 * 决定渲染哪个版本。source 仅用于埋点与排障，不参与业务判断。
 * 降级态（V2 渲染抛错 / 契约校验失败）强制回 V1（§6 自动降级行）。
 */
export function resolveViewVersion({
  v2Bundled = false,
  status = null,
  preference = null,
  degraded = false,
} = {}) {
  if (degraded) return { version: VIEW_V1, source: 'DEGRADED' };
  if (!v2Bundled) return { version: VIEW_V1, source: 'V2_NOT_BUNDLED' };
  if (!status || status.ok !== true) {
    return {
      version: VIEW_V1,
      source: 'STATUS_UNAVAILABLE',
      reason: status && status.reason,
    };
  }
  if (status.data.available !== true) {
    return {
      version: VIEW_V1,
      source: 'SERVER_NOT_AVAILABLE',
      reason: status.data.reason || null,
    };
  }
  if (preference && preference.version === VIEW_V1) {
    return { version: VIEW_V1, source: 'LOCAL_OVERRIDE' };
  }
  if (preference && preference.version === VIEW_V2) {
    return { version: VIEW_V2, source: 'LOCAL_OVERRIDE' };
  }
  const serverDefault =
    status.data.defaultVersion === VIEW_V2 ? VIEW_V2 : VIEW_V1;
  return { version: serverDefault, source: 'SERVER_DEFAULT' };
}
